import axios from "axios";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { setupSessionExpirySync } from "../services/sessionService";

const OAuthComplete = ({ user }) => {
    const navigate = useNavigate();
    const [pendingRoute, setPendingRoute] = useState(null);

    // Navigate once pendingRoute is set.
    // For protected routes (e.g. /dashboard), wait until App.js confirms user state.
    // For public routes (e.g. account-creation pages), navigate immediately.
    useEffect(() => {
        if(!pendingRoute) return;

        const isProtectedRoute =
            pendingRoute === "/dashboard" || pendingRoute === "/home";

        if(isProtectedRoute) {
            if(user?.id) navigate(pendingRoute);
        } else {
            navigate(pendingRoute);
        }
    }, [pendingRoute, user, navigate]);

    useEffect(() => {
        const handleOAuthComplete = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const token = params.get("token");

                if(!token) {
                    return navigate("/login");
                }

                // Save token
                localStorage.setItem("session_id", token);

                // Decode token
                const decoded = jwtDecode(token);
                const { id, email, role } = decoded || {};

                if(!email || !role) {
                    return navigate("/login");
                }
                // Save user basic info
                localStorage.setItem("userEmail", email);
                const { isProfileVerified } = decoded;
                localStorage.setItem(
                    "user",
                    JSON.stringify({ id, email, role, isProfileVerified }),
                );

                // Set a flag in localStorage to indicate the user is logged in
                // This helps components like Topbar avoid unnecessary API calls
                localStorage.setItem("userLoggedIn", "true");

                // Start session expiry synchronization
                setupSessionExpirySync();

                // Fetch full user info for form prefill
                try {
                    console.log("Fetching user info for role:", role);
                    const res = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/auth/${role}-info?email=${email}`,
                        { withCredentials: true },
                    );
                    const data = res.data;

                    console.log("User info fetch successful:", data);
                    console.log(
                        "Submitted status:",
                        data.submitted,
                        "Type:",
                        typeof data.submitted,
                    );
                    // Store form data for prefill
                    localStorage.setItem(
                        `${role}FormData`,
                        JSON.stringify(data),
                    );

                    // Determine navigation route
                    let targetRoute;
                    if(role === "entrepreneur") {
                        targetRoute =
                            data.submitted === 1
                                ? "/dashboard"
                                : "/entrepreneur-account-creation";
                        console.log(
                            "Entrepreneur navigation decision: submitted =",
                            data.submitted,
                            "→",
                            targetRoute,
                        );
                    } else if(role === "investor") {
                        targetRoute =
                            data.application_submitted === 1
                                ? "/dashboard"
                                : "/investor-account-creation";
                        console.log(
                            "Investor navigation decision: application_submitted =",
                            data.application_submitted,
                            "→",
                            targetRoute,
                        );
                    } else {
                        targetRoute = "/login";
                    }

                    console.log("Navigation target route:", targetRoute);

                    // Fetch full user data and dispatch login event for ALL routes
                    // (not just dashboard), so App.js user state is populated before navigation
                    try {
                        const userRes = await axios.get(
                            `${process.env.REACT_APP_BACKEND_URL}/api/auth/user`,
                            {
                                withCredentials: true,
                                timeout: 5000,
                            },
                        );

                        if(userRes.data?.id) {
                            // Dispatch custom event to update global user state
                            window.dispatchEvent(
                                new CustomEvent("user-logged-in", {
                                    detail: userRes.data,
                                }),
                            );

                            // Signal to navigate — actual navigation happens in useEffect
                            // once App.js has updated the user state
                            setPendingRoute(targetRoute);
                        } else {
                            navigate(targetRoute);
                        }
                    } catch(err) {
                        // If user data refresh fails, still navigate but log the error
                        console.error("Failed to refresh user data:", err);
                        navigate(targetRoute);
                    }
                } catch(err) {
                    console.error(
                        "Failed to fetch user info for role:",
                        role,
                        err,
                    );
                    console.log("Error status:", err.response?.status);
                    console.log("Error message:", err.message);
                    console.log("Error response data:", err.response?.data);

                    // If user info fetch fails, navigate to account creation page for new users
                    let defaultRoute;
                    if(role === "entrepreneur") {
                        defaultRoute = "/entrepreneur-account-creation";
                    } else if(role === "investor") {
                        defaultRoute = "/investor-account-creation";
                    } else {
                        defaultRoute = "/login";
                    }
                    console.log(
                        "User info fetch failed, navigating to default route:",
                        defaultRoute,
                    );
                    navigate(defaultRoute);
                }
            } catch(err) {
                console.error("OAuth post-login flow failed:", err);
                navigate("/login");
            }
        };

        handleOAuthComplete();
    }, [navigate, setPendingRoute]);

    return <p>Completing login...</p>;
};

export default OAuthComplete;